// Single row in the employee constraint table. Renders as read, edit, or add depending on
// props/state, always through the same grid cells — this keeps the three states pixel-aligned
// by construction instead of relying on three separate components to agree on spacing.
import { useState } from "react";
import { Constraint } from "../../types/constraint";
import labels from "../../utils/labels";
import dateUtil from "../../utils/dateUtil";
import constraintStyle from "../../styles/constraint.module.css";
import { ReactComponent as DeleteIcon } from "../../assets/icons/bin.svg";
import { ReactComponent as EditIcon } from "../../assets/icons/edit.svg";
import DeleteConstraintModal from "./DeleteConstraintModal";
import IconButtonComponent from "../UI/buttons/IconButton";
import AddConstraintSearch from "./AddConstraintSearch";
import ConstraintComment from "./ConstraintComment";
import DatePicker from "../UI/input/DatePicker";
import StandardButton from "../UI/buttons/StandardButton";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { getCitizenAddress } from "../../service/citizenAddress";
import ConstraintService from "../../service/constraintService";
import {
  doPeriodsOverlap,
  getTakenTargetIds,
} from "../../service/constraintDuplicates";
import {
  addConstraintToEmployee,
  updateConstraintOnEmployee,
} from "../../store/constraint";

const ConstraintRow = ({
  constraint,
  isCategoryConstraint,
  employeeRef,
  existingConstraints,
  onDelete,
  onAddCancel,
}: {
  // Present for an existing hensyn (read/edit); absent for the add row.
  constraint?: Constraint;
  // Only meaningful — and required — for the add row, which has no constraint to read it from.
  isCategoryConstraint?: boolean;
  employeeRef: string;
  existingConstraints: Constraint[];
  onDelete?: () => void;
  onAddCancel?: () => void;
}) => {
  const dispatch = useAppDispatch();
  const clients = useAppSelector((state) => state.constraintSlice.clients);

  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [startDate, setStartDate] = useState(constraint?.startDate ?? "");
  const [endDate, setEndDate] = useState(constraint?.endDate ?? "");
  const [comment, setComment] = useState(constraint?.comment ?? "");
  const [selectedTargetId, setSelectedTargetId] = useState<number | undefined>(
    undefined,
  );
  const [isPending, setIsPending] = useState(false);

  const isFormMode = constraint === undefined || isEditing;
  const rowIsCategoryConstraint =
    constraint?.isCategoryConstraint ?? isCategoryConstraint ?? false;

  const targetAddress =
    constraint && !rowIsCategoryConstraint
      ? getCitizenAddress(clients, constraint.targetId)
      : undefined;

  const clashingConstraint = constraint
    ? existingConstraints.find(
        (other) =>
          other.id !== constraint.id &&
          other.targetId === constraint.targetId &&
          other.isCategoryConstraint === constraint.isCategoryConstraint &&
          doPeriodsOverlap(
            other.startDate,
            other.endDate,
            startDate || undefined,
            endDate || undefined,
          ),
      )
    : existingConstraints.find(
        (other) =>
          other.targetId === selectedTargetId &&
          other.isCategoryConstraint === rowIsCategoryConstraint &&
          doPeriodsOverlap(
            other.startDate,
            other.endDate,
            startDate || undefined,
            endDate || undefined,
          ),
      );

  const canSave = constraint ? true : selectedTargetId !== undefined;

  const resetEditState = () => {
    setStartDate(constraint?.startDate ?? "");
    setEndDate(constraint?.endDate ?? "");
    setComment(constraint?.comment ?? "");
  };

  const handleCancel = () => {
    if (!constraint) {
      onAddCancel?.();
      return;
    }
    resetEditState();
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!canSave || clashingConstraint) return;
    setIsPending(true);
    try {
      if (!constraint) {
        const created = await ConstraintService.addEmployeeConstraint(
          {
            employeeRef,
            isCategoryConstraint: rowIsCategoryConstraint,
            targetId: selectedTargetId!,
            startDate: startDate || undefined,
            endDate: endDate || undefined,
            comment: comment || undefined,
          },
          dispatch,
        );
        dispatch(addConstraintToEmployee({ employeeRef, constraint: created }));
        onAddCancel?.();
      } else {
        const updated = await ConstraintService.editEmployeeConstraint(
          {
            id: constraint.id,
            employeeRef,
            isCategoryConstraint: constraint.isCategoryConstraint,
            targetId: constraint.targetId,
            startDate: startDate || undefined,
            endDate: endDate || undefined,
            comment: comment || undefined,
          },
          dispatch,
        );
        dispatch(
          updateConstraintOnEmployee({ employeeRef, constraint: updated }),
        );
        setIsEditing(false);
      }
    } catch {
      // error notification handled in service
    }
    setIsPending(false);
  };

  return (
    <div
      className={`${constraintStyle.dataTableRow} ${isFormMode ? constraintStyle.staticRow : ""}`}
      style={{ paddingLeft: "var(--padding-2xl)" }}
    >
      {constraint ? (
        <p>
          {constraint.targetName}
          {targetAddress && (
            <span style={{ color: "var(--col-text-secondary)" }}>
              {" "}
              {targetAddress}
            </span>
          )}
        </p>
      ) : (
        <AddConstraintSearch
          isCategoryConstraint={rowIsCategoryConstraint}
          onSelect={(id) => setSelectedTargetId(id)}
          excludedTargetIds={getTakenTargetIds(
            existingConstraints,
            rowIsCategoryConstraint,
            startDate || undefined,
            endDate || undefined,
          )}
        />
      )}

      {!constraint || isEditing ? (
        <DatePicker
          id={`constraint-start-date-${constraint?.id ?? "add"}`}
          value={startDate}
          onDateChange={setStartDate}
          max={endDate || undefined}
          placeholder={labels.selectStartDate}
        />
      ) : constraint.startDate ? (
        <p>
          <span style={{ color: "var(--col-text-secondary)" }}>
            {labels.start}{" "}
          </span>
          {dateUtil.getCalendarDate(new Date(constraint.startDate))}
        </p>
      ) : (
        <p /> //insert empty p tag to ensure correct spacing
      )}

      {!constraint || isEditing ? (
        <DatePicker
          id={`constraint-end-date-${constraint?.id ?? "add"}`}
          value={endDate}
          onDateChange={setEndDate}
          min={startDate || undefined}
          placeholder={labels.selectEndDate}
        />
      ) : constraint.endDate ? (
        <p>
          <span style={{ color: "var(--col-text-secondary)" }}>
            {labels.end}{" "}
          </span>
          {dateUtil.getCalendarDate(new Date(constraint.endDate))}
        </p>
      ) : (
        <p /> //insert empty p tag to ensure correct spacing
      )}

      {!constraint || isEditing ? (
        <ConstraintComment comment={comment} isCreate onChange={setComment} />
      ) : constraint.comment ? (
        <p className={constraintStyle.comment}>{constraint.comment}</p>
      ) : (
        <p />
      )}

      <p className={constraintStyle.author} title={constraint?.author}>
        {constraint?.author}
      </p>

      {!constraint || isEditing ? (
        <div
          className={`${constraintStyle.formActions} flex justify-between gap-[var(--spacing-base)] items-center`}
        >
          <StandardButton
            variant="TERTIARY"
            onClick={handleCancel}
            ariaLabel={labels.cancel}
          >
            {labels.cancel}
          </StandardButton>
          <StandardButton
            variant="PRIMARY"
            onClick={handleSave}
            ariaLabel={labels.save}
            disable={!canSave || clashingConstraint !== undefined}
            pending={isPending}
          >
            {labels.save}
          </StandardButton>
        </div>
      ) : (
        <>
          <div className={constraintStyle.actionColumn}>
            <IconButtonComponent
              id={`edit-employee-constraint-${constraint.id}`}
              icon={<EditIcon width={24} height={24} />}
              onClick={() => setIsEditing(true)}
              ariaLabel={labels.editConstraint}
            />
          </div>
          <div className={constraintStyle.actionColumn}>
            <IconButtonComponent
              id={`delete-employee-constraint-${constraint.id}`}
              icon={<DeleteIcon width={24} height={24} />}
              onClick={() => setShowDeleteModal(true)}
              ariaLabel={labels.deleteConstraint}
            />
          </div>
        </>
      )}

      {showDeleteModal && (
        <DeleteConstraintModal
          onDelete={() => {
            onDelete?.();
            setShowDeleteModal(false);
          }}
          onDiscard={() => setShowDeleteModal(false)}
        />
      )}
      {clashingConstraint && (
        <p
          className="text-red-500 text-sm pb-[var(--spacing-sm)]"
          style={{ paddingLeft: "var(--padding-2xl)" }}
        >
          {labels.errorConstraintAlreadyExistsWithName(
            clashingConstraint.targetName,
          )}
        </p>
      )}
    </div>
  );
};

export default ConstraintRow;
